import {DashboardState, TileId} from './dashboardModel';
import {
  canAccessPreviewTiles,
  LIVE_TILE_DEFAULT_HIDDEN,
  LIVE_TILE_ORDER,
  LIVE_TILE_PREVIEW_USER_ONLY,
  LIVE_TILES,
  LiveDashboardContext,
  TileContentStatus,
} from './tileCatalog';

export type {LiveDashboardContext, LiveSectionId, TileContentStatus} from './tileCatalog';
export type {LiveTileConfig} from './tileCatalog';
export {LIVE_TILE_DEFAULT_HIDDEN, LIVE_TILE_ORDER, LIVE_TILES} from './tileCatalog';

/** Whether the tile has real content, is still loading, or is empty for this viewer. */
export const tileContentStatus = (
  tileId: TileId,
  state: DashboardState,
  ctx: LiveDashboardContext
): TileContentStatus =>
  LIVE_TILES.find((tile) => tile.id === tileId)?.status(state, ctx) ?? 'empty';

export const buildLiveTileStatuses = (
  state: DashboardState,
  ctx: LiveDashboardContext
): Record<TileId, TileContentStatus> => {
  const statuses = {} as Record<TileId, TileContentStatus>;
  LIVE_TILE_ORDER.forEach((tileId) => {
    statuses[tileId] = tileContentStatus(tileId, state, ctx);
  });
  return statuses;
};

export interface TileVisibilityOptions {
  remembered: ReadonlySet<TileId>;
  isFirstEverLoad: boolean;
  /** Meet Someone and Jira stay off the board for everyone except the preview user. */
  userId?: string;
  /** User explicitly removed this tile in customize mode. */
  hidden?: ReadonlySet<TileId>;
  /** User explicitly added or kept this tile in customize mode. */
  layoutPinned?: ReadonlySet<TileId>;
}

/** Whether a live tile should be mounted in the grid for this load. */
export const isLiveTileVisible = (
  status: TileContentStatus,
  tileId: TileId,
  options: TileVisibilityOptions
): boolean => {
  if (options.hidden?.has(tileId)) {
    return false;
  }
  if (LIVE_TILE_PREVIEW_USER_ONLY.has(tileId) && !canAccessPreviewTiles(options.userId)) {
    return false;
  }
  const pinned = options.layoutPinned?.has(tileId) ?? false;
  // Default-hidden tiles stay off the board (even with content) until the viewer
  // explicitly adds/keeps them in customize mode.
  if (LIVE_TILE_DEFAULT_HIDDEN.has(tileId) && !pinned) {
    return false;
  }
  if (status === 'present') {
    return true;
  }
  if (status === 'empty') {
    return false;
  }
  return options.isFirstEverLoad || options.remembered.has(tileId) || pinned;
};

/** Visible live tiles in global `LIVE_TILE_ORDER`. */
export const getVisibleLiveTilesInOrder = (
  state: DashboardState,
  ctx: LiveDashboardContext,
  visibility: TileVisibilityOptions
): TileId[] =>
  LIVE_TILE_ORDER.filter((tileId) => {
    const status = tileContentStatus(tileId, state, ctx);
    return isLiveTileVisible(status, tileId, visibility);
  });
